import React from "react";
import SEO from "@/lib/seo-static.json";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, FileText, PlayCircle, Download } from "lucide-react";
import { SITE } from "@/lib/site";
import { Container, Section, SectionHeader, Reveal, Kicker, usePageTitle } from "@/components/ui-kit";
import { CaseStudies, CTABand } from "@/components/sections/Social";

const GUIDES = [
  { icon: BookOpen, tag: "Playbook", title: "The 06:30 briefing: how field teams start the day without the office", desc: "Why a morning email beats a morning phone call, and how to set it up per tenant timezone.", read: "6 min" },
  { icon: FileText, tag: "Guide", title: "Designing an emergency escalation ladder that actually fires", desc: "Tiers, windows, on-call rotas and what to post in Slack when Tier 2 triggers.", read: "8 min" },
  { icon: BookOpen, tag: "Playbook", title: "Bulk-onboarding 400 suppliers without an admin team", desc: "CSV shape, invite templates, the public /apply page and the compliance hard-block.", read: "7 min" },
  { icon: FileText, tag: "Guide", title: "Markup A vs Markup B: pricing maintenance work you don't do yourself", desc: "How property managers separate their fee from the client uplift — and explain both.", read: "5 min" },
  { icon: PlayCircle, tag: "Video", title: "Product tour: quote → job → invoice → paid in 4 minutes", desc: "One thread, one click at each stage, Stripe at the end.", read: "4 min" },
  { icon: Download, tag: "Template", title: "Gas Safety (CP12) job-sheet template", desc: "Import into Job Sheets & Forms and auto-attach to every gas job.", read: "Download" },
];

export default function Resources() {
  usePageTitle(SEO["/resources"].title, SEO["/resources"].description, SEO["/resources"].image);
  return (
    <div data-testid="resources-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <Container className="relative pt-14 pb-10 lg:pt-20">
          <Kicker>Resources</Kicker>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-mk-text leading-[1] text-balance max-w-4xl">Playbooks from teams who run on minutes.</h1>
          <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-2xl">Guides, templates and stories on running maintenance like a platform company. Subscribe to the dispatch in the footer for the monthly digest.</p>
        </Container>
      </section>

      <Section className="pt-8">
        <Container>
          <SectionHeader eyebrow="Guides & playbooks" title="Learn the operating model, not just the buttons." />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {GUIDES.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.05}>
                <Link to="/contact?tab=contact" data-testid={`resource-${i}`} className="card-dark group block p-6 h-full">
                  <div className="flex items-center justify-between"><span className="grid place-items-center h-10 w-10 rounded-xl border border-mk-border bg-mk-bg text-mk-lime"><g.icon className="h-5 w-5" /></span><span className="font-mono text-[10px] uppercase tracking-wider text-mk-muted">{g.tag} · {g.read}</span></div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-mk-text group-hover:text-mk-lime transition-colors">{g.title}</h3>
                  <p className="mt-2 text-sm text-mk-dim leading-relaxed">{g.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-mk-text">Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <CaseStudies />

      <Section>
        <Container>
          <Reveal className="rounded-3xl border border-mk-border bg-mk-surface p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div><div className="eyebrow">Sandbox</div><h2 className="mt-3 font-display text-3xl font-bold text-mk-text">The best resource is the product.</h2><p className="mt-3 text-mk-dim">Every trial workspace comes pre-loaded with demo jobs, suppliers, clients, contracts and invoices — so you can try every playbook above on realistic data before you import your own.</p></div>
            <div className="flex flex-wrap gap-3 lg:justify-end"><a href={SITE.signupUrl} target="_blank" rel="noreferrer" data-testid="resources-sandbox-btn" className="btn-lime">Open the sandbox <ArrowRight className="h-4 w-4" /></a><Link to="/product" className="btn-ghost">Browse modules</Link></div>
          </Reveal>
        </Container>
      </Section>

      <CTABand />
    </div>
  );
}
